import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileShell from "@/components/MobileShell";
import AdminDisplayPreference, { adminShellClassName, useAdminDisplayPreference } from "@/components/AdminDisplayPreference";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { ArrowLeft, AlertTriangle, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [activityData, setActivityData] = useState(null);
  const [displayPreference, setDisplayPreference] = useAdminDisplayPreference();
  const [smtpTestOpen, setSmtpTestOpen] = useState(false);
  const [smtpDestination, setSmtpDestination] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpMessage, setSmtpMessage] = useState("");

  const loadActivity = async () => {
    try {
      const { data } = await api.get("/admin/activity");
      setActivityData(data);
    } catch (err) {
      setActivityData(null);
    }
  };

  useEffect(() => {
    const params = search.trim() ? { q: search.trim() } : {};
    api.get("/admin/users", { params }).then((r) => setUsers(r.data));
  }, [search]);

  useEffect(() => {
    loadActivity();
  }, []);

  const openUser = async (id) => {
    const { data } = await api.get(`/admin/user/${id}`);
    setSelected(data);
  };

  const openPatientPlans = (patientId) => {
    if (patientId) navigate(`/admin/patients/${patientId}/plans`);
  };

  const sendSmtpTest = async (event) => {
    event.preventDefault();
    setSmtpTesting(true); setSmtpMessage("");
    try {
      await api.post("/admin/smtp-test", { destination_email: smtpDestination });
      setSmtpMessage("Test email accepted by the mail service.");
    } catch (err) {
      setSmtpMessage(err.response?.data?.detail || "Unable to send the SMTP test email.");
    } finally {
      setSmtpTesting(false);
    }
  };

  if (selected) {
    const u = selected.user;
    return (
      <MobileShell contentClassName={adminShellClassName(displayPreference)}>
        <header className="px-6 pt-8 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <button onClick={() => setSelected(null)} className="text-slate-500 mb-3" data-testid="admin-back-btn"><ArrowLeft size={20} /></button>
              <h1 className="text-2xl font-bold font-display">{u.name}</h1>
              <p className="text-sm text-slate-500">{u.email}</p>
            </div>
            <AdminDisplayPreference value={displayPreference} onChange={setDisplayPreference} />
          </div>
        </header>
        <section className="px-6" data-testid="admin-user-detail">
          <PatientDetail selected={selected} onOpenPlans={() => navigate(`/admin/patients/${u.id}/plans`)} />
        </section>
      </MobileShell>
    );
  }

  return (
    <MobileShell contentClassName={adminShellClassName(displayPreference)}>
      <header className="px-6 pt-8 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <button onClick={() => navigate("/")} className="text-slate-500 mb-3" data-testid="admin-home-btn"><ArrowLeft size={20} /></button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">{isAdmin ? "Clinic admin" : "Clinician workspace"}</p>
            <h1 className="text-2xl font-bold font-display text-slate-900 mt-1">Patients</h1>
            <p className="text-sm text-slate-500 mt-1">{users.length} active</p>
          </div>
          <div className="flex gap-2">
            <AdminDisplayPreference value={displayPreference} onChange={setDisplayPreference} />
            {isAdmin
              ? <Button size="sm" variant="secondary" onClick={() => navigate("/admin/patients")}>Patients</Button>
              : <Button size="sm" variant="secondary" onClick={() => navigate("/my-patients")}>My Patients</Button>}
            {isAdmin && <Button size="sm" variant="secondary" onClick={() => navigate("/admin/therapists")}>Therapists</Button>}
            {isAdmin && <Button size="sm" variant="secondary" onClick={() => navigate("/admin/feedback")}>Beta Feedback</Button>}
            {isAdmin && <Button size="sm" variant="secondary" onClick={() => { setSmtpMessage(""); setSmtpTestOpen(true); }}>Test Email Delivery</Button>}
            <Button size="sm" variant="secondary" onClick={() => navigate("/admin/exercises")}>Exercise Library</Button>
            <Button size="sm" variant="secondary" onClick={() => navigate("/admin/playlists")}>Playlists</Button>
          </div>
        </div>
      </header>
      <section className="px-6 pb-8 grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="space-y-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className="rounded-xl bg-white"
            data-testid="admin-patient-search"
          />
          <PatientList users={users} onOpenUser={openUser} />
        </aside>
        <section className="space-y-3" data-testid="admin-command-centre">
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate("/admin/feedback")}
              className="w-full rounded-2xl border border-slate-100 bg-white p-4 text-left active:scale-[0.99] transition"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-teal-50 text-teal-700 grid place-items-center">
                  <MessageSquare size={18} />
                </span>
                <span className="flex-1">
                  <span className="block font-semibold text-slate-900">Beta feedback</span>
                  <span className="block text-sm text-slate-500">Review app feedback from patient beta testers</span>
                </span>
              </div>
            </button>
          )}
          <RecentActivity activityData={activityData} loadActivity={loadActivity} openUser={openUser} openPatientPlans={openPatientPlans} />
          <div className="grid gap-3 xl:grid-cols-3">
            <Worklist
              title="No active home plan"
              rows={activityData?.patients_no_active_home_plan || []}
              empty="All searched patients have active home plans."
              actionLabel="Add exercises"
              onAction={(row) => openPatientPlans(row.id)}
            />
            <Worklist
              title="Needs review"
              rows={activityData?.plans_needing_review || []}
              empty="No plans need review."
              actionLabel="Edit plan"
              onAction={(row) => openPatientPlans(row.patient?.id || row.patient_user_id)}
              getLabel={(row) => row.title}
              getMeta={(row) => `${row.patient?.name || "Patient"}${row.review_date ? ` - ${row.review_date}` : ""}`}
            />
            <Worklist
              title="Recently edited plans"
              rows={activityData?.recently_edited_plans || []}
              empty="No recently edited plans."
              actionLabel="Edit plan"
              onAction={(row) => openPatientPlans(row.patient?.id || row.patient_user_id)}
              getLabel={(row) => row.title}
              getMeta={(row) => `${row.patient?.name || "Patient"} - ${row.plan_type === "home_plan" ? "Home Plan" : "Program"}`}
            />
          </div>
        </section>
      </section>
      <Dialog open={smtpTestOpen} onOpenChange={setSmtpTestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Email Delivery</DialogTitle>
            <DialogDescription>Send a basic SMTP test email. No patient information or program link is included.</DialogDescription>
          </DialogHeader>
          <form onSubmit={sendSmtpTest} className="space-y-4">
            <label className="block text-sm font-medium text-slate-700" htmlFor="smtp-test-destination">Destination email</label>
            <Input id="smtp-test-destination" type="email" value={smtpDestination} onChange={(event) => setSmtpDestination(event.target.value)} required autoComplete="email" />
            {smtpMessage && <p role="status" className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">{smtpMessage}</p>}
            <Button type="submit" disabled={smtpTesting}>{smtpTesting ? "Sending..." : "Send test email"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </MobileShell>
  );
}

function RecentActivity({ activityData, loadActivity, openUser, openPatientPlans }) {
  const [expandedPatientId, setExpandedPatientId] = useState("");
  const rows = activityData?.patient_activity || [];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Command centre</p>
          <h2 className="text-lg font-bold text-slate-900 mt-1">Recent patient activity</h2>
        </div>
        <Button size="sm" variant="outline" onClick={loadActivity}>Refresh</Button>
      </div>
      <div className="mt-3 grid gap-2">
        {rows.slice(0, 10).map((item) => {
          const patientId = item.patient?.id || item.id;
          const isExpanded = expandedPatientId === patientId;
          return (
            <div key={patientId} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto]">
                <button
                  type="button"
                  onClick={() => setExpandedPatientId(isExpanded ? "" : patientId)}
                  className="text-left"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-sm font-semibold text-slate-900">{item.patient?.name || "Unknown patient"}</div>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {item.updates_count || 0} updates
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-slate-700">Latest: {item.latest_label || "No recent updates"}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {painSummary(item)}
                    {item.last_activity_at ? `${painSummary(item) ? " - " : ""}Last activity: ${formatDateTime(item.last_activity_at)}` : ""}
                  </div>
                  {item.attention_reasons?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {item.attention_reasons.slice(0, 4).map((reason) => (
                        <span key={reason} className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
                <div className="flex flex-wrap content-start gap-2">
                  {patientId && <Button size="sm" variant="outline" onClick={() => openUser(patientId)}>View patient</Button>}
                  {patientId && <Button size="sm" variant="outline" onClick={() => openPatientPlans(patientId)}>Edit plan</Button>}
                  {patientId && <Button size="sm" variant="outline" onClick={() => openPatientPlans(patientId)}>Add exercises</Button>}
                  {patientId && <Button size="sm" variant="outline" onClick={() => openUser(patientId)}>Review progress</Button>}
                </div>
              </div>
              {isExpanded && (
                <div className="mt-3 border-t border-slate-200 pt-3">
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Activity history</div>
                  <div className="mt-2 space-y-2">
                    {(item.events || []).map((event) => (
                      <div key={event.id} className="rounded-lg bg-white px-3 py-2 text-xs text-slate-600">
                        <span className="font-semibold text-slate-900">{event.label}</span>
                        {event.created_at ? ` - ${formatDateTime(event.created_at)}` : ""}
                        {event.pain_score != null ? ` - Pain: ${event.pain_score}/10` : ""}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {activityData && rows.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">No recent activity yet.</div>
        )}
      </div>
    </div>
  );
}

function PatientList({ users, onOpenUser }) {
  return (
    <ul className="space-y-3" data-testid="admin-user-list">
      {users.map((u) => (
        <li key={u.id}>
          <button
            onClick={() => onOpenUser(u.id)}
            data-testid={`admin-user-${u.id}`}
            className="w-full text-left bg-white rounded-2xl border border-slate-100 p-4 active:scale-[0.99]"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">{u.name}</div>
                <div className="text-xs text-slate-500">{u.email}</div>
                <div className="text-xs text-slate-600 mt-1">{u.condition || "No condition selected"}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Sessions</div>
                <div className="font-bold text-slate-900">{u.sessions_completed}</div>
              </div>
            </div>
            <div className="mt-2 flex gap-2 text-[10px] font-semibold uppercase tracking-wide">
              {u.has_red_flag && <span className="rounded-full bg-rose-100 text-rose-700 px-2 py-0.5">Red flag</span>}
              {u.flare_up_alert && <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5">Flare-up</span>}
              {u.last_pain != null && <span className="rounded-full bg-teal-50 text-teal-700 px-2 py-0.5">Pain: {u.last_pain}</span>}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function PatientDetail({ selected, onOpenPlans }) {
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card label="Condition" value={selected.intake?.condition || "Not set"} />
      <Card label="Main goal" value={selected.intake?.main_goal} />
      <Card label="Function limitation" value={selected.intake?.function_limitation} />
      {selected.intake?.has_red_flag && (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4 flex gap-3 lg:col-span-2" data-testid="admin-redflag">
          <AlertTriangle size={20} className="text-rose-600 shrink-0" />
          <div>
            <div className="font-semibold text-rose-900">Red-flag alert</div>
            <div className="text-sm text-rose-800 mt-1">{(selected.intake.red_flags || []).join(", ")}</div>
          </div>
        </div>
      )}
      <Card label="Sessions completed" value={selected.sessions?.length || 0} />
      <Card label="Check-ins" value={selected.progress?.length || 0} />
      <Card label="Badges" value={(selected.badges || []).map((b) => b.title).join(", ") || "None"} />
      <Button onClick={onOpenPlans} className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white lg:col-span-2">
        Exercise plans
      </Button>
    </div>
  );
}

function Worklist({ title, rows, empty, actionLabel, onAction, getLabel, getMeta }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4">
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{title}</div>
      <div className="mt-3 space-y-2">
        {rows.slice(0, 4).map((row) => (
          <div key={row.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
            <div className="text-sm font-semibold text-slate-900">{getLabel ? getLabel(row) : row.name}</div>
            <div className="text-xs text-slate-500 mt-1">{getMeta ? getMeta(row) : row.email}</div>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => onAction(row)}>{actionLabel}</Button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-slate-500">{empty}</div>}
      </div>
    </div>
  );
}

function Card({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">{label}</div>
      <div className="text-sm text-slate-900 mt-1">{String(value)}</div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function painSummary(item) {
  if (item.latest_pain == null) return "";
  if (item.previous_pain == null || item.pain_change == null) return `Pain: ${item.latest_pain}/10`;
  if (item.pain_change === 0) return `Pain: ${item.latest_pain}/10, unchanged`;
  const direction = item.pain_change > 0 ? "up" : "down";
  return `Pain: ${item.latest_pain}/10, ${direction} from ${item.previous_pain}/10`;
}
